import { distance, length as lineLength, lineString, nearestPointOnLine, point } from "@turf/turf";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { drawnItineraryGeometrySlice, navigationSessionSlice, useEphemeralSelector } from "../../store/ephemeral";
import { type ItineraryDTO, type ItineraryLegDTO } from "../../types/dtos/api/itinerary/itinerary.dto";

interface NavigationTopPanelProps { "itinerary": ItineraryDTO | null }

function formatDistance( meters: number ): string {
    if ( !isFinite( meters ) || meters < 0 ) {
        return "—";
    }
    if ( meters >= 1000 ) {
        return `${ ( meters / 1000 ).toFixed( 1 ) } km`;
    }

    return `${ Math.round( meters ) } m`;
}

function formatDuration( seconds: number ): string {
    if ( !isFinite( seconds ) || seconds < 0 ) {
        return "—";
    }
    const s = Math.round( seconds );
    const h = Math.floor( s / 3600 );
    const m = Math.floor( ( s % 3600 ) / 60 );
    const sec = s % 60;
    if ( h > 0 ) {
        return `${ h }h ${ m }m`;
    }
    if ( m > 0 ) {
        return `${ m }m ${ sec }s`;
    }

    return `${ sec }s`;
}

function formatSpeed( kmh: number | null | undefined ): string {
    if ( kmh == null || !isFinite( kmh ) || kmh < 0 ) {
        return "—";
    }

    return `${ Math.round( kmh ) } km/h`;
}

function interpolateTime(
    markers: Array<{
        "shapeIndex": number;
        "time":       string;
        "cumDist":    number;
    }>,
    currentDist: number,
): number | null {
    if ( markers.length === 0 ) {
        return null;
    }

    // If before first or after last, clamp
    if ( currentDist <= markers[0].cumDist ) {
        return new Date( markers[0].time ).getTime();
    }
    if ( currentDist >= markers[markers.length - 1].cumDist ) {
        return new Date( markers[markers.length - 1].time ).getTime();
    }

    // Binary search for surrounding markers
    let lo = 0;
    let hi = markers.length - 1;
    while ( lo <= hi ) {
        const mid = ( lo + hi ) >> 1;
        const d = markers[mid].cumDist;
        if ( d === currentDist ) {
            return new Date( markers[mid].time ).getTime();
        }
        if ( d < currentDist ) {
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    const i2 = lo; // first > current
    const i1 = lo - 1; // last <= current
    const m1 = markers[i1];
    const m2 = markers[i2];
    const t1 = new Date( m1.time ).getTime();
    const t2 = new Date( m2.time ).getTime();
    const f = ( currentDist - m1.cumDist ) / Math.max( 1, m2.cumDist - m1.cumDist );

    return t1 + f * ( t2 - t1 );
}

export const NavigationTopPanel: React.FC<NavigationTopPanelProps> = ( { itinerary } ) => {
    const following = useEphemeralSelector( navigationSessionSlice, ( session ) => session.routeLineFollowing );
    const active = useEphemeralSelector( drawnItineraryGeometrySlice, ( geometry ) => geometry );

    const roundUpTo10 = ( v: number | null ): number | null => {
        if ( v == null ) {
            return null;
        }

        return Math.ceil( v / 10 ) * 10;
    };

    /*
     * Hooks must be declared unconditionally at top-level
     * Average GPS speed accumulator and state
     * Average GPS speed accumulator
     */
    const gpsAggRef = useRef<{
        "startTs": number | null;
        "lastPos": [number, number] | null;
        "cumDist": number;
    }>( {
        "startTs": null,
        "lastPos": null,
        "cumDist": 0,
    } );
    const [ avgGpsKmh, setAvgGpsKmh ] = useState<number | null>( null );
    const lastLegIdRef = useRef<string | null>( null );

    const computed = useMemo( () => {
        if ( following == null || active == null || itinerary == null ) {
            return null;
        }
        if ( following.lineId == null || following.lineId === "" ) {
            return null;
        }

        // Find active polyline by id
        const poly = active.polylines.find( ( p ) => p.id === following.lineId );
        if ( poly == null ) {
            return null;
        }

        // Find matching leg strictly by legId set on polyline
        const polyTyped = poly as Record<string, unknown> & { "legId"?: string };
        const leg: ItineraryLegDTO | undefined = itinerary.legs.find( ( l ) => l.id !== null && l.id !== undefined && l.id === polyTyped.legId );
        if ( leg === null || leg === undefined ) {
            return null;
        }

        // Validate poly.coordinates before creating lineString
        if ( !Array.isArray( poly.coordinates ) || poly.coordinates.length === 0 ) {
            return null;
        }

        // Validate all coordinates in poly.coordinates are valid numbers
        const invalidCoordIndex = poly.coordinates.findIndex( ( coord: unknown ) => {
            if ( !Array.isArray( coord ) || coord.length !== 2 ) {
                return true;
            }

            return typeof coord[0] !== "number" || typeof coord[1] !== "number" ||
              isNaN( coord[0] ) || isNaN( coord[1] ) || !isFinite( coord[0] ) || !isFinite( coord[1] );
        } );
        if ( invalidCoordIndex !== -1 ) {
            return null;
        }

        // Build a lineString and project current position (use matched when onLine, else raw)
        const line = lineString( poly.coordinates );
        const projCoord = ( following.onLine ? following.matchedPosition : following.rawPosition ) as [number, number] | undefined;
        if ( projCoord == null ) {
            return null;
        }

        // Validation des coordonnées avant d'appeler nearestPointOnLine
        if ( typeof projCoord[0] !== "number" || typeof projCoord[1] !== "number" ||
          isNaN( projCoord[0] ) || isNaN( projCoord[1] ) || !isFinite( projCoord[0] ) || !isFinite( projCoord[1] ) ) {
            console.warn( "[NavigationTopPanel] Invalid coordinates for nearestPointOnLine:", projCoord );

            return null;
        }

        const snapped = nearestPointOnLine( line, {
            "type":        "Point",
            "coordinates": projCoord,
        } );
        const props = ( snapped.properties ?? {} ) as { "location"?: number };
        const locationKm = typeof props.location === "number" ? props.location : 0;
        const totalDist = Math.max( 0, lineLength( line ) * 1000 ); // meters from geometry for consistency
        const currentDist = Math.max( 0, Math.min( totalDist, locationKm * 1000 ) );

        // Remaining distance
        const remainingDist = Math.max( 0, totalDist - currentDist );

        // Remaining duration using timeMarkers if present, else proportion of distance
        let remainingSec: number | null = null;
        let theoKmh: number | null = null;
        if ( Array.isArray( leg.timeMarkers ) && leg.timeMarkers.length >= 2 ) {
            const markers = leg.timeMarkers.slice().sort( ( a, b ) => a.cumDist - b.cumDist );
            const currentTs = interpolateTime( markers, currentDist );
            const endTs = new Date( markers[markers.length - 1].time ).getTime();
            if ( currentTs != null ) {
                remainingSec = Math.max( 0, Math.round( ( endTs - currentTs ) / 1000 ) );
            }

            /*
             * Theoretical instantaneous speed from time markers: use surrounding segment
             * Find indices i such that currentDist in [markers[i].cumDist, markers[i+1].cumDist]
             */
            let i = 0;
            for ( let j = 0; j < markers.length - 1; j++ ) {
                if ( currentDist >= markers[j].cumDist && currentDist <= markers[j + 1].cumDist ) {
                    i = j;
                    break;
                }

                // If past last segment, use the last
                if ( currentDist > markers[markers.length - 1].cumDist ) {
                    i = markers.length - 2;
                }
            }
            const m1 = markers[i];
            const m2 = markers[Math.min( i + 1, markers.length - 1 )];
            const dt = ( new Date( m2.time ).getTime() - new Date( m1.time ).getTime() ) / 1000; // s
            const dd = Math.max( 0, m2.cumDist - m1.cumDist ); // m
            if ( dt > 0 && dd > 0 ) {
                theoKmh = ( dd / dt ) * 3.6;
            }
        }
        if ( remainingSec == null ) {
            const totalSec = typeof leg.duration === "number" ? leg.duration : 0;
            const ratio = totalDist > 0 ? currentDist / totalDist : 0;
            remainingSec = Math.max( 0, Math.round( totalSec * ( 1 - ratio ) ) );

            // Fallback theoretical speed if no time markers: planned average
            if ( theoKmh == null && totalSec > 0 && totalDist > 0 ) {
                theoKmh = ( totalDist / totalSec ) * 3.6;
            }
        }

        // Direction constraint: reversed > 30s
        // eslint-disable-next-line react-hooks/purity
        const now = Date.now();
        const reversedForMs = following.isReversed ? now - following.lastDirectionChangeTime : 0;
        const showReversed = following.isReversed && reversedForMs > 30000;

        return {
            remainingDist,
            remainingSec,
            showReversed,
            currentDist,
            "nowTs": now,
            theoKmh,
            "legId": leg.id ?? null,
        };
    }, [ following, active, itinerary ] );

    // No projection-based instantaneous speed; theoretical instantaneous is derived from timeMarkers inside computed

    // Average speed based on GPS (raw positions)
    useEffect( () => {
        if ( following == null ) {
            return;
        }

        // Reset accumulator when leg changes
        if ( computed?.legId !== lastLegIdRef.current ) {
            gpsAggRef.current = {
                "startTs": null,
                "lastPos": null,
                "cumDist": 0,
            };
            setAvgGpsKmh( null );
            lastLegIdRef.current = computed?.legId ?? null;
        }
        const raw = following.rawPosition as [number, number] | undefined;
        if ( raw == null ) {
            return;
        }
        const now = Date.now();
        const agg = gpsAggRef.current;
        if ( agg.startTs == null ) {
            agg.startTs = now;
        }
        if ( agg.lastPos != null ) {
            const dKm = distance( point( agg.lastPos ), point( raw ) ); // km
            const dMeters = Math.max( 0, dKm * 1000 );

            // filter out jumps > 300m in one tick (optional smoothing)
            if ( dMeters < 300 ) {
                agg.cumDist += dMeters;
            }
        }
        agg.lastPos = raw;
        if ( agg.startTs != null ) {
            const elapsedSec = ( now - agg.startTs ) / 1000;
            if ( elapsedSec > 5 ) {
                setAvgGpsKmh( ( agg.cumDist / elapsedSec ) * 3.6 );
            }
        }
    }, [ following, computed?.legId ] );

    if ( computed == null ) {
        return null;
    }

    return (
        <div style={{
            "position":     "absolute",
            "top":          8,
            "left":         "50%",
            "transform":    "translateX(-50%)",
            "zIndex":       20,
            "minWidth":     260,
            "maxWidth":     "90%",
            "background":   "rgba(24,24,24,0.9)",
            "color":        "white",
            "borderRadius": 10,
            "padding":      "8px 12px",
            "boxShadow":    "0 2px 10px rgba(0,0,0,0.3)",
        }}
        >
            <div style={{
                "display":        "flex",
                "alignItems":     "center",
                "justifyContent": "space-between",
                "gap":            12,
            }}
            >
                <div style={{ "fontWeight": 700 }}>
                    {formatDistance( computed.remainingDist )}
                    {" "}
                    ·
                    {formatDuration( computed.remainingSec )}
                </div>
                <div style={{
                    "fontSize": 12,
                    "opacity":  0.9,
                }}
                >
                    Inst théo:
                    {" "}
                    {formatSpeed( roundUpTo10( computed.theoKmh ) )}
                    {" "}
                    · Moy GPS:
                    {formatSpeed( avgGpsKmh )}
                </div>
                {computed.showReversed &&
                  <div style={{
                      "background":   "#ff4d4f",
                      "color":        "white",
                      "padding":      "2px 8px",
                      "borderRadius": 6,
                      "fontSize":     12,
                      "fontWeight":   700,
                  }}
                  >
                      Sens opposé depuis 30s+
                  </div>}
            </div>
        </div>
    );
};

export default NavigationTopPanel;
